import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { useDashboard } from '../store/useDashboard';
import api from '../services/api';
import FormulaireMemo from '../components/FormulaireMemo';
import ListeParFiliale from '../components/ListeParFiliale';
import useEstMobile from '../hooks/useEstMobile';
import TirerPourRafraichir from '../components/TirerPourRafraichir';
import { LABELS_ROLES } from '../constants/roles';
import MenuProfil from '../components/MenuProfil';
import BandeauDelegation from '../components/BandeauDelegation';
import {
  IconeListe, IconeNote, IconeAvion, IconeRecu, IconeRafraichir, IconeDocument,
  IconeCloche, IconeFeu, IconeHorloge, IconeValide, IconeRefuse, IconeDossier,
  IconeCrayon, IconeTrombone, IconePlume, IconeAlerte, IconeCadenas,
} from '../components/IconesSVG';

const TYPES_DOCS = {
  expression_besoin: { label: 'Fiche EB',        icon: IconeListe, color: '#2980B9' },
  bon_commande:      { label: 'Bon commande',     icon: IconeNote, color: '#8E44AD' },
  frais_mission:     { label: 'Frais mission',    icon: IconeAvion, color: '#E67E22' },
  facture:           { label: 'Facture',          icon: IconeRecu, color: '#16A085' },
  regularisation:    { label: 'Regularisation',   icon: IconeRafraichir, color: '#935116' },
};

const ROLES_HOLDING = ['preparateur_memo_holding', 'assistante_pdg', 'pdg'];

const CONFIG_ROLE = {
  responsable_service:      { statut: 'en_controle_terrain',     label: 'Responsable Service',     actions: { approuver: true,  rejeter: true,  upload: false,           signer: false } },
  acheteur:                 { statut: 'en_controle_terrain',     label: 'Acheteur',                actions: { approuver: true,  rejeter: true,  upload: false,           signer: false } },
  direction_achats:         { statut: 'en_memo_filiale',         label: 'Direction Achats',        actions: { approuver: true,  rejeter: true,  upload: false,           signer: false } },
  preparateur_memo_filiale: { statut: 'en_memo_filiale',         label: 'Prep. Memo Filiale',      actions: { approuver: false, rejeter: false, upload: 'memo_filiale',  signer: false } },
  dg_filiale:               { statut: 'en_signature_dg_filiale', label: 'DG Filiale',              actions: { approuver: false, rejeter: true,  upload: false,           signer: 'dg'  } },
  preparateur_memo_holding: { statut: 'envoye_holding',          label: 'Prep. Memo Holding',      actions: { approuver: false, rejeter: false, upload: 'memo_holding',  signer: false } },
  assistante_pdg:            { statut: 'en_validation_assistante_pdg', label: 'Assistante Direction PDG', actions: { approuver: true, rejeter: true, upload: false, signer: false } },
  responsable:               { statut: 'en_attente_responsable',       label: 'Responsable Zone',         actions: { approuver: true,  rejeter: true,  upload: false, signer: false } },
};

const getToken = () => {
  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      const val = localStorage.getItem(key);
      if (val?.startsWith('eyJ')) return val;
      try { const p = JSON.parse(val); return p?.state?.token || p?.token; } catch { }
    }
  } catch { }
  return null;
};

export default function ParapheurRolePage() {
  const navigate = useNavigate();
  const { allerDashboard } = useDashboard();
  const { user } = useAuthStore();

  // Liste
  const [pochettes, setPochettes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  // PDF
  const [ongletPDF, setOngletPDF] = useState(0);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPDF, setLoadingPDF] = useState(false);

  // Actions
  const [actionMode, setActionMode] = useState(null);
  const [commentaire, setCommentaire] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [memoUploade, setMemoUploade] = useState(false);
  const [showFormulaireMemo, setShowFormulaireMemo] = useState(false);
  const [donneesMemo, setDonneesMemo] = useState(null);

  // Signature DG — drag & drop sur le PDF
  const [signatureBase64, setSignatureBase64] = useState(null);
  const [previewPdfBase64, setPreviewPdfBase64] = React.useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = React.useState(null); // apercu signe affiche dans le viewer
  const [infoSignataire, setInfoSignataire] = React.useState(null);
  const [sigZone, setSigZone] = useState({ x: 60, y: 300, w: 220, h: 85 });
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const pdfViewerRef = useRef(null);
  const chargementPdfRef = useRef(0); // invalide les chargements PDF obsoletes
  const fileInputRef = useRef(null);

  const roleUser = user?.roles?.[0]?.role || user?.role;
  const config = CONFIG_ROLE[roleUser] || {};
  const estMobile = useEstMobile();
  // Sur mobile l'ecran est trop etroit pour juxtaposer liste et detail : on
  // bascule de l'une a l'autre (maitre-detail).
  const m = (base, surcharge) => (estMobile ? { ...base, ...surcharge } : base);
  const actions = config.actions || {};

  // eslint-disable-next-line react-hooks/exhaustive-deps -- effet volontairement limite au montage
  useEffect(() => { charger(); }, []);

  // Ecouteur focus retire : meme cause de bug que sur DetailDemandePage.

  const charger = async () => {
    try {
      setLoading(true);
      const res = await api.get('/demandes?statut=' + (config.statut || ''));
      setPochettes(res.data?.data || res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const ouvrirPochette = async (p) => {
    setMemoUploade((p.documents || []).some(d => d.categorie === (CONFIG_ROLE[roleUser]?.actions?.upload)));
    setSelected(p);
    setActionMode(null);
    setCommentaire('');
    setError('');
    setSuccess('');
    reinitialiserPreview();
    setOngletPDF(0);
    const docs = p.documents || [];
    if (docs.length > 0) chargerPDF(docs[0]);
    else setPdfUrl(null);
  };

  const chargerPDF = async (doc) => {
    // Chaque chargement porte un numero : si un autre onglet est demande avant la
    // fin du fetch, le blob obsolete n'atteindra jamais l'etat et doit etre libere
    // sur place (le useEffect ci-dessous ne libere que ce qui a ete affiche).
    const generation = ++chargementPdfRef.current;
    setLoadingPDF(true);
    setPdfUrl(null);
    try {
      const token = getToken();
      const response = await fetch((process.env.REACT_APP_STATIC_URL || 'http://localhost:5001') + '/' + doc.chemin, {
        headers: token ? { 'Authorization': 'Bearer ' + token } : {}
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      if (generation !== chargementPdfRef.current) { URL.revokeObjectURL(url); return; }
      setPdfUrl(url);
    } catch (err) { console.error(err); }
    finally { if (generation === chargementPdfRef.current) setLoadingPDF(false); }
  };

  // Le PDF arrive en base64 : passer par un Uint8Array, sinon les octets > 127
  // seraient reencodes en UTF-8 par le Blob et le fichier serait corrompu.
  const base64VersBlobUrl = (base64) => {
    const binaire = atob(base64);
    const octets = new Uint8Array(binaire.length);
    for (let i = 0; i < binaire.length; i++) octets[i] = binaire.charCodeAt(i);
    return URL.createObjectURL(new Blob([octets], { type: 'application/pdf' }));
  };

  // Libere l'apercu signe et revient au document d'origine
  const reinitialiserPreview = () => {
    setPreviewBlobUrl(null);
    setPreviewPdfBase64(null);
    setInfoSignataire(null);
    setSignatureBase64(null);
  };

  const changerOnglet = (idx) => {
    setOngletPDF(idx);
    const docs = selected?.documents || [];
    if (docs[idx]) chargerPDF(docs[idx]);
  };

  // ===== DRAG ZONE SIGNATURE =====
  const onMouseDownDrag = useCallback((e) => {
    if (e.target.dataset.resize) return;
    e.preventDefault();
    setDragging(true);
    setDragStart({ x: e.clientX - sigZone.x, y: e.clientY - sigZone.y });
  }, [sigZone]);

  const onMouseMove = useCallback((e) => {
    if (!dragging && !resizing) return;
    e.preventDefault();
    const container = pdfViewerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();

    if (dragging) {
      const newX = Math.max(0, Math.min(e.clientX - dragStart.x, rect.width - sigZone.w));
      const newY = Math.max(0, Math.min(e.clientY - dragStart.y, rect.height - sigZone.h));
      setSigZone(p => ({ ...p, x: newX, y: newY }));
    }
    if (resizing) {
      const newW = Math.max(100, Math.min(e.clientX - rect.left - sigZone.x, 350));
      const newH = Math.max(50, Math.min(e.clientY - rect.top - sigZone.y, 200));
      setSigZone(p => ({ ...p, w: newW, h: newH }));
    }
  }, [dragging, resizing, dragStart, sigZone]);

  const onMouseUp = useCallback(() => {
    setDragging(false);
    setResizing(false);
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  // Liberer l'apercu signe : remplacement, retour au document d'origine, demontage
  useEffect(() => {
    if (!previewBlobUrl) return undefined;
    return () => URL.revokeObjectURL(previewBlobUrl);
  }, [previewBlobUrl]);

  // Liberer le PDF affiche : changement d'onglet, changement de dossier, demontage
  useEffect(() => {
    if (!pdfUrl) return undefined;
    return () => URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  // Au demontage, invalider tout chargement en cours : son blob se liberera seul
  useEffect(() => () => { chargementPdfRef.current = -1; }, []);

  // Fermeture du dossier (les nombreux setSelected(null) differes) : le viewer
  // disparait, plus rien ne doit retenir le blob
  useEffect(() => { if (!selected) setPdfUrl(null); }, [selected]);

  // ===== ACTIONS =====
  const approuver = async () => {
    setSaving(true); setError('');
    try {
      await api.post('/demandes/' + selected.id + '/visa', {
        type_visa: 'approbation', commentaire: commentaire || 'Approuve',
      });
      setSuccess('Dossier transmis à l\'étape suivante');
      setActionMode(null);
      setPochettes(prev => prev.filter(po => po.id !== selected.id));
      charger();
      setTimeout(() => { setSelected(null); setSuccess(''); }, 2000);
    } catch (err) {
      if (err.response?.status === 409) {
        setError((err.response?.data?.message || 'Dossier déjà traité par quelqu\'un d\'autre.'));
        setPochettes(prev => prev.filter(po => po.id !== selected.id));
        setTimeout(() => { setSelected(null); charger(); }, 1800);
      } else {
        setError(err.response?.data?.message || err.message);
      }
    }
    finally { setSaving(false); }
  };

  const rejeter = async () => {
    if (!commentaire.trim()) { setError('Motif obligatoire'); return; }
    setSaving(true); setError('');
    try {
      await api.post('/demandes/' + selected.id + '/visa', {
        type_visa: 'rejet', commentaire,
      });
      setSuccess('Dossier rejeté');
      setPochettes(prev => prev.filter(po => po.id !== selected.id));
      charger();
      setTimeout(() => { setSelected(null); setSuccess(''); }, 2000);
    } catch (err) {
      if (err.response?.status === 409) {
        setError((err.response?.data?.message || 'Dossier déjà traité par quelqu\'un d\'autre.'));
        setPochettes(prev => prev.filter(po => po.id !== selected.id));
        setTimeout(() => { setSelected(null); charger(); }, 1800);
      } else {
        setError(err.response?.data?.message || err.message);
      }
    }
    finally { setSaving(false); }
  };

  const uploaderMemo = async (file) => {
    setSaving(true); setError('');
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('categorie', actions.upload);
      await api.post('/demandes/' + selected.id + '/documents', formData);
      setMemoUploade(true);
      setSuccess('Mémo uploadé — vous pouvez maintenant transmettre');

      // Recharger le detail de la pochette pour recuperer le nouveau document,
      // puis pointer automatiquement le viewer sur ce document
      const detailRes = await api.get('/demandes/' + selected.id);
      const pochetteFraiche = detailRes.data?.data || detailRes.data;
      const docsFrais = pochetteFraiche?.documents || [];
      const idxNouveauDoc = docsFrais.findIndex(d => d.categorie === actions.upload);

      setSelected(pochetteFraiche);
      if (idxNouveauDoc >= 0) {
        setOngletPDF(idxNouveauDoc);
        chargerPDF(docsFrais[idxNouveauDoc]);
      }

      charger();
    } catch (err) {
      if (err.response?.status === 409) {
        setError((err.response?.data?.message || 'Dossier déjà traité par quelqu\'un d\'autre.'));
        setPochettes(prev => prev.filter(po => po.id !== selected.id));
        setTimeout(() => { setSelected(null); charger(); }, 1800);
      } else {
        setError(err.response?.data?.message || err.message);
      }
    }
    finally { setSaving(false); }
  };

  const transmettreMemo = async () => {
    setSaving(true); setError('');
    try {
      await api.post('/demandes/' + selected.id + '/visa', {
        type_visa: 'approbation', commentaire: commentaire || 'Memo uploade et transmis',
      });
      setSuccess('Dossier transmis a l\'etape suivante');
      setMemoUploade(false);
      setPochettes(prev => prev.filter(po => po.id !== selected.id));
      charger();
      setTimeout(() => { setSelected(null); setSuccess(''); }, 2000);
    } catch (err) {
      if (err.response?.status === 409) {
        setError((err.response?.data?.message || 'Dossier déjà traité.'));
        setPochettes(prev => prev.filter(po => po.id !== selected.id));
        setTimeout(() => { setSelected(null); charger(); }, 1800);
      } else {
        setError(err.response?.data?.message || err.message);
      }
    }
    finally { setSaving(false); }
  };

  // Charger la signature uploadee depuis le profil utilisateur
  const chargerSignatureUploadee = async () => {
    setError('');
    try {
      // L'intercepteur axios renvoie deja response.data : le payload est a plat.
      const res = await api.get('/demandes/' + selected.id + '/preview-signature');
      if (res?.success) {
        setPreviewPdfBase64(res.pdf_base64);
        setInfoSignataire(res.signataire);
        setSignatureBase64('preview');
        // Afficher le PDF signe dans le viewer, a la place du memo d'origine
        setPreviewBlobUrl(base64VersBlobUrl(res.pdf_base64));
        const docs = selected?.documents || [];
        const memoIdx = docs.findIndex(d => d.categorie === 'memo_filiale');
        if (memoIdx >= 0) changerOnglet(memoIdx);
      }
    } catch(e) {
      setError(e.response?.data?.message || e.message || 'Erreur chargement signature');
    }
  };

  const confirmerSignatureDG = async () => {
    if (!signatureBase64 || !previewPdfBase64) return;
    setSaving(true); setError('');
    try {
      // Le PDF signe est regenere et persiste cote serveur (route visa) : inutile
      // de renvoyer l'apercu, la route repart des donnees_memo du document.
      await api.post('/demandes/' + selected.id + '/visa', {
        type_visa: 'signature_dg',
        commentaire: commentaire || 'Signé par le DG Filiale',
      });
      reinitialiserPreview();
      setCommentaire('');
      await charger();
      setSelected(null);
    } catch(e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  const formatMontant = (m) => m ? Number(m).toLocaleString('fr-FR') + ' F' : '—';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
  const pCount = pochettes.length;
  const p1Count = pochettes.filter(p => p.priorite === 'P1').length;

  return (
    <div style={s.page}>
      {/* TOPBAR */}
      <div style={s.topbar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src="/logo.png" alt="JETA" style={{ height: 28 }} onError={e => { e.target.style.display = 'none'; }} />
          <div onClick={allerDashboard} title="Retour au tableau de bord" style={{ cursor: 'pointer' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>
              Parapheur — {LABELS_ROLES[roleUser] || config.label || roleUser}
            </div>
            <div style={{ fontSize: 11, color: '#8899AA' }}>
              {user?.prenom} {user?.nom} · {user?.roles?.[0]?.filiale?.nom}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {pCount > 0 && (
            <div style={{ background: '#C0392B', borderRadius: 20, padding: '6px 14px', color: '#fff', fontSize: 13, fontWeight: 700 }}>
              <IconeCloche taille={14} /> {pCount} en attente
              {p1Count > 0 && <span style={{ marginLeft: 8, background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: 10, fontSize: 11 }}><IconeFeu taille={11} /> {p1Count} urgent{p1Count > 1 ? 's' : ''}</span>}
            </div>
          )}
          <MenuProfil couleurAvatar={config.color || '#C0392B'} />
        </div>
      </div>

      {/* MAIN */}
      <div style={m(s.main, { flexDirection: "column" })} className="avec-nav-mobile">
        {/* LISTE */}
        <div style={m(s.liste, { width: "100%", display: selected ? "none" : "flex", flex: 1 })}>
          <div style={s.listeTitre}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Dossiers à traiter</span>
            <span style={{ fontSize: 11, background: '#F0F0F0', padding: '2px 8px', borderRadius: 10, color: '#555' }}>{pCount}</span>
          </div>
          <TirerPourRafraichir onRafraichir={charger} style={{ flex: 1 }}>
            {loading ? (
              <div style={s.emptyState}><IconeHorloge taille={16} /> Chargement...</div>
            ) : pCount === 0 ? (
              <div style={s.emptyState}>
                <div style={{ marginBottom: 10, color: '#1E8449' }}><IconeValide taille={40} /></div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0D1B2A', marginBottom: 4 }}>Vous êtes à jour !</div>
                <div style={{ fontSize: 12, color: '#888' }}>Aucun dossier en attente</div>
              </div>
            ) : ROLES_HOLDING.includes(roleUser) ? (
              // ROLES HOLDING → regroupement par filiale
              <div style={{ padding: '8px' }}>
                <ListeParFiliale
                  pochettes={pochettes}
                  onSelect={ouvrirPochette}
                  selected={selected}
                  renderCard={(p, isSelected) => {
                    const typeInfo = TYPES_DOCS[p.type_document] || { icon: IconeDocument, color: '#666' };
                    const prioBg = p.priorite === 'P1' ? '#FDEDEC' : p.priorite === 'P3' ? '#EAF4FB' : '#FEF9E7';
                    const prioColor = p.priorite === 'P1' ? '#C0392B' : p.priorite === 'P3' ? '#2980B9' : '#9A7D0A';
                    return (
                      <div key={p.id} onClick={() => ouvrirPochette(p)}
                        style={{ ...s.listeItem, ...(isSelected ? { background: '#FDEDEC', borderLeft: '3px solid #C0392B' } : { borderLeft: '3px solid transparent' }) }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ color: typeInfo.color, display: 'inline-flex' }}><typeInfo.icon taille={18} /></span>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: isSelected ? '#C0392B' : '#0D1B2A' }}>{p.reference}</div>
                              <div style={{ fontSize: 10, color: '#888' }}>{formatDate(p.created_at)}</div>
                            </div>
                          </div>
                          <span style={{ fontSize: 10, background: prioBg, color: prioColor, padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>
                            {p.priorite || 'P2'}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: '#555', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.titre || p.motif || '—'}
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#C0392B' }}>{formatMontant(p.montant)}</span>
                      </div>
                    );
                  }}
                />
              </div>
            ) : pochettes.map(p => {
              // AUTRES ROLES → liste classique
              const typeInfo = TYPES_DOCS[p.type_document] || { icon: IconeDocument, color: '#666' };
              const isSelected = selected?.id === p.id;
              const prioBg = p.priorite === 'P1' ? '#FDEDEC' : p.priorite === 'P3' ? '#EAF4FB' : '#FEF9E7';
              const prioColor = p.priorite === 'P1' ? '#C0392B' : p.priorite === 'P3' ? '#2980B9' : '#9A7D0A';
              return (
                <div key={p.id} onClick={() => ouvrirPochette(p)}
                  style={{ ...s.listeItem, ...(isSelected ? { background: '#FDEDEC', borderLeft: '3px solid #C0392B' } : { borderLeft: '3px solid transparent' }) }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: typeInfo.color, display: 'inline-flex' }}><typeInfo.icon taille={18} /></span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: isSelected ? '#C0392B' : '#0D1B2A' }}>{p.reference}</div>
                        <div style={{ fontSize: 10, color: '#888' }}>{formatDate(p.created_at)}</div>
                      </div>
                    </div>
                    <span style={{ fontSize: 10, background: prioBg, color: prioColor, padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>
                      {p.priorite || 'P2'}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#555', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.titre || p.motif || '—'}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, background: '#0D1B2A', color: '#fff', padding: '1px 8px', borderRadius: 6, fontWeight: 700 }}>
                      {p.filiale?.code || '—'}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#C0392B' }}>{formatMontant(p.montant)}</span>
                  </div>
                </div>
              );
            })}
          </TirerPourRafraichir>
        </div>

        {/* SPLIT SCREEN */}
        {!selected ? (
          <div style={s.splitVide}>
            <div style={{ opacity: 0.15, marginBottom: 16 }}><IconeDossier taille={64} /></div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0D1B2A', marginBottom: 8 }}>Sélectionnez un dossier</div>
            <div style={{ fontSize: 13, color: '#888' }}>Cliquez sur un dossier dans la liste</div>
          </div>
        ) : (
          <div style={m(s.split, { flexDirection: "column" })}>
            {estMobile && (
              <button type="button" onClick={() => setSelected(null)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#1A202C',
                         color: '#A0AEC0', border: 'none', borderBottom: '1px solid #4A5568',
                         padding: '12px 16px', fontSize: 13, fontFamily: 'inherit',
                         cursor: 'pointer', minHeight: 44, flexShrink: 0 }}>
                ← Retour à la liste
              </button>
            )}

            {/* PDF VIEWER GAUCHE */}
            <div style={m(s.splitLeft, {
              // Le PDF est le contenu principal du parapheur : on lui laisse
              // la hauteur d ecran restante, deduction faite de l entete, des
              // onglets et de la barre de navigation basse. 42vh obligeait a
              // defiler pour lire la moindre ligne.
              minHeight: "calc(100dvh - 260px)", flex: "none",
            })}>
              {/* Onglets */}
              <div style={s.onglets}>
                {(selected.documents || []).map((doc, i) => (
                  <button key={i} onClick={() => changerOnglet(i)}
                    style={{ ...s.onglet, ...(ongletPDF === i ? s.ongletActif : {}) }}>
                    {doc.categorie === 'document_principal' ? <><IconeListe taille={13} /> Demande terrain</> :
                     doc.categorie === 'memo_filiale' ? <><IconeCrayon taille={13} /> Mémo Filiale</> :
                     doc.categorie === 'memo_holding' ? <><IconeDocument taille={13} /> Mémo Holding</> :
                     <><IconeTrombone taille={13} /> {(doc.nom || '').substring(0, 16) + '...'}</>}
                  </button>
                ))}
                {(selected.documents || []).length === 0 && (
                  <div style={{ padding: '8px 16px', fontSize: 12, color: '#888' }}>Aucun document</div>
                )}

                {/* Instruction si mode placement signature */}
                {signatureBase64 && !previewBlobUrl && (
                  <div style={{ marginLeft: 'auto', padding: '6px 14px', background: '#C0392B', fontSize: 11, color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                    Déplacez la zone rouge pour placer votre signature
                  </div>
                )}
                {previewBlobUrl && (
                  <div style={{ marginLeft: 'auto', padding: '6px 14px', background: '#27AE60', fontSize: 11, color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <IconePlume taille={13} /> Aperçu signé — non encore transmis
                  </div>
                )}
              </div>

              {/* Zone PDF avec overlay signature */}
              <div ref={pdfViewerRef} style={s.pdfZone}>
                {loadingPDF && !previewBlobUrl ? (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
                    <IconeHorloge taille={14} /> Chargement du PDF...
                  </div>
                ) : (previewBlobUrl || pdfUrl) ? (
                  <>
                    <iframe src={previewBlobUrl || pdfUrl} style={{ flex: 1, border: 'none', display: 'block', width: '100%', height: '100%' }}
                      title={previewBlobUrl ? 'Apercu signe' : 'Document'} />

                    {/* Zone signature draggable — seulement pour une signature dessinee :
                        l'apercu serveur contient deja la signature incrustee dans le PDF. */}
                    {signatureBase64 && !previewBlobUrl && (
                      <div
                        style={{
                          position: 'absolute',
                          left: sigZone.x,
                          top: sigZone.y,
                          width: sigZone.w,
                          height: sigZone.h,
                          border: '2px dashed #C0392B',
                          background: 'rgba(192,57,43,0.06)',
                          cursor: dragging ? 'grabbing' : 'grab',
                          userSelect: 'none',
                          zIndex: 10,
                          borderRadius: 4,
                        }}
                        onMouseDown={onMouseDownDrag}
                      >
                        {/* Bande titre */}
                        <div style={{ background: '#C0392B', padding: '3px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 9, color: '#fff', fontWeight: 700 }}><IconePlume taille={9} /> VISA DG FILIALE</span>
                          <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)' }}>← Déplacez →</span>
                        </div>

                        {/* Aperçu signature */}
                        <img src={signatureBase64} alt="sig"
                          style={{ width: '100%', height: sigZone.h - 32, objectFit: 'contain', padding: '4px', pointerEvents: 'none', display: 'block' }} />

                        {/* Nom + date */}
                        <div style={{ position: 'absolute', bottom: 2, left: 6, right: 24, fontSize: 8, color: '#C0392B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {user?.prenom} {user?.nom} · {new Date().toLocaleDateString('fr-FR')}
                        </div>

                        {/* Poignée resize */}
                        <div
                          data-resize="true"
                          style={{ position: 'absolute', right: -5, bottom: -5, width: 16, height: 16, background: '#C0392B', borderRadius: 3, cursor: 'se-resize', zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); setResizing(true); }}
                        >
                          <span style={{ fontSize: 10, color: '#fff', pointerEvents: 'none' }}>⤡</span>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
                    <div style={{ marginBottom: 12, opacity: 0.3 }}><IconeDocument taille={48} /></div>
                    <div style={{ fontSize: 13 }}>Aucun document à afficher</div>
                  </div>
                )}
              </div>
            </div>

            {/* BLOC DÉCISION DROITE */}
            <div style={m(s.splitRight, { width: "100%", borderLeft: "none", flex: 1 })}>
              {/* Header */}
              <div style={s.decisionHeader}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>{selected.reference}</div>
                    <div style={{ fontSize: 12, color: '#8899AA', marginTop: 4 }}>
                      {selected.filiale?.code} · {selected.filiale?.nom}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>
                      {selected.montant ? Number(selected.montant).toLocaleString('fr-FR') : '—'}
                    </div>
                    <div style={{ fontSize: 10, color: '#8899AA' }}>FCFA</div>
                  </div>
                </div>
              </div>

              <div style={s.decisionBody}>
                {/* Résumé */}
                <div style={s.resumeGrid}>
                  {[
                    { label: 'Demandeur', value: (selected.demandeur?.prenom || '') + ' ' + (selected.demandeur?.nom || '') },
                    { label: 'Type', value: TYPES_DOCS[selected.type_document]?.label || selected.type_document },
                    { label: 'Priorite', value: selected.priorite || 'P2' },
                    { label: 'Documents', value: (selected.documents || []).length + ' fichier(s)' },
                  ].map((item, i) => (
                    <div key={i} style={s.resumeItem}>
                      <div style={s.resumeLabel}>{item.label}</div>
                      <div style={s.resumeValue}>{item.value || '—'}</div>
                    </div>
                  ))}
                </div>

                {selected.titre && (
                  <div style={{ background: '#F9FAFB', borderRadius: 8, padding: '10px 12px', marginBottom: 14, border: '0.5px solid #e5e7eb', fontSize: 12, color: '#555' }}>
                    <div style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>Objet</div>
                    {selected.titre}
                  </div>
                )}

                {success && <div style={s.successMsg}>{success}</div>}
                {error && <div style={s.errorMsg}>{error}</div>}

                {/* ===== UPLOAD MEMO (2 etapes) ===== */}
                {actions.upload && !actionMode && (
                  <div style={s.actionSection}>
                    <div style={s.actionTitle}>
                      {actions.upload === 'memo_filiale' ? <><IconeCrayon taille={14} /> Mémo Filiale</> : <><IconeDocument taille={14} /> Mémo Holding</>}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 22, height: 22, borderRadius: '50%', background: memoUploade ? '#27AE60' : '#C0392B', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                        {memoUploade ? <IconeValide taille={13} /> : '1'}
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700 }}>Uploader le mémo</span>
                    </div>

                    {!memoUploade && (
                      <div style={{ fontSize: 12, color: '#888', marginBottom: 10, lineHeight: 1.5 }}>
                        Rédigez votre mémo, convertissez en PDF et uploadez-le ici.
                      </div>
                    )}

                    {/* Upload manuel masque temporairement - uniquement le formulaire de generation actif
                    <button type="button" onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      style={{ ...s.btnUpload, background: memoUploade ? '#F0F0F0' : s.btnUpload.background, color: memoUploade ? '#888' : '#fff' }}>
                      <IconeTrombone taille={14} /> {saving && !memoUploade ? 'Upload...' : memoUploade ? 'Changer le mémo' : 'Choisir le PDF mémo'}
                    </button>
                    */}
                    <input ref={fileInputRef} type="file" accept=".pdf" style={{ display: 'none' }}
                      onChange={e => { if (e.target.files[0]) { uploaderMemo(e.target.files[0]); } e.target.value = ''; }} />

                    <button type="button" onClick={() => setShowFormulaireMemo(true)}
                      style={{ ...s.btnUpload, background: memoUploade ? '#F0F0F0' : s.btnUpload.background, color: memoUploade ? '#888' : '#fff' }}>
                      <IconeCrayon taille={14} /> {memoUploade ? 'Changer le mémo' : 'Générer le mémo'}
                    </button>

                    {showFormulaireMemo && (
                      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
                        <div style={{ maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
                          <FormulaireMemo
                            key={'memo-' + selected.id + '-' + showFormulaireMemo}
                            type_memo={actions.upload === 'memo_filiale' ? 'filiale' : 'holding'}
                            filialeId={selected.filiale_id || selected.filiale?.id}
                            pochetteId={selected.id}
                            demande={selected}
                            valeursInitiales={donneesMemo}
                            onAnnuler={() => setShowFormulaireMemo(false)}
                            onGenere={async (doc, numero, valeursUtilisees) => {
                              setShowFormulaireMemo(false);
                              setMemoUploade(true);
                              setDonneesMemo(valeursUtilisees);
                              setSuccess('Mémo généré avec succès — vous pouvez maintenant transmettre');

                              // Recharger le detail pour recuperer le document genere,
                              // puis l'afficher directement dans le viewer PDF.
                              try {
                                const detailRes = await api.get('/demandes/' + selected.id);
                                const pochetteFraiche = detailRes.data;
                                const docsFrais = pochetteFraiche?.documents || [];
                                const categorieRecherchee = actions.upload;
                                const idxNouveauDoc = docsFrais.findIndex(d => d.categorie === categorieRecherchee);
                                setSelected(pochetteFraiche);
                                if (idxNouveauDoc >= 0) {
                                  setOngletPDF(idxNouveauDoc);
                                  chargerPDF(docsFrais[idxNouveauDoc]);
                                }
                              } catch (e) { console.error(e); }

                              charger();
                            }}
                          />
                        </div>
                      </div>
                    )}

                    <div style={{ height: 1, background: '#E5E7EB', margin: '14px 0' }} />

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 22, height: 22, borderRadius: '50%', background: memoUploade ? '#C0392B' : '#E5E7EB', color: memoUploade ? '#fff' : '#aaa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                        2
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: memoUploade ? '#0D1B2A' : '#aaa' }}>Transmettre</span>
                    </div>

                    {!memoUploade ? (
                      <div style={{ fontSize: 11, color: '#aaa', marginBottom: 10 }}>
                        <IconeCadenas taille={13} /> Uploadez d'abord le mémo pour débloquer cette action
                      </div>
                    ) : (
                      <textarea style={s.textarea}
                        placeholder="Commentaire optionnel..."
                        value={commentaire}
                        onChange={e => setCommentaire(e.target.value)} />
                    )}

                    <button type="button" onClick={transmettreMemo} disabled={!memoUploade || saving}
                      style={{ ...s.btnApprouver, background: memoUploade ? '#27AE60' : '#E5E7EB', color: memoUploade ? '#fff' : '#aaa', cursor: memoUploade ? 'pointer' : 'not-allowed' }}>
                      {saving && memoUploade ? <><IconeHorloge taille={14} />...</> : <><IconeValide taille={14} /> Transmettre</>}
                    </button>

                    {actions.rejeter && (
                      <button type="button" onClick={() => setActionMode('rejeter')} style={s.btnRejeterSm}><IconeRefuse taille={13} /> Rejeter le dossier</button>
                    )}
                  </div>
                )}

                {/* ===== SIGNATURE DG ===== */}
                {actions.signer === 'dg' && !actionMode && !signatureBase64 && (
                  <div style={s.actionSection}>
                    <BandeauDelegation />
                    <div style={s.actionTitle}><IconePlume taille={14} /> Signer le Mémo Filiale</div>
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 12, lineHeight: 1.5 }}>
                      Cliquez sur "Dessiner ma signature", puis <strong>placez-la</strong> sur le mémo à l'endroit souhaité.
                    </div>
                    <textarea style={s.textarea}
                      placeholder="Commentaire optionnel..."
                      value={commentaire} onChange={e => setCommentaire(e.target.value)} />
                      <button type='button' onClick={chargerSignatureUploadee} style={s.btnSigner}>
                        <IconePlume taille={14} /> Apposer ma signature
                      </button>
                    <button type="button" onClick={() => setActionMode('rejeter')} style={s.btnRejeterSm}><IconeRefuse taille={13} /> Rejeter</button>
                  </div>
                )}

                {/* ===== CONFIRMER SIGNATURE DG ===== */}
                {actions.signer === 'dg' && signatureBase64 && !actionMode && (
                  <div style={{ background: '#F0FFF4', border: '1px solid #27AE60', borderRadius: 12, padding: 16, marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1E8449', marginBottom: 12 }}>
                      <IconeValide taille={13} /> Signature prête — prévisualisation
                    </div>
                    {infoSignataire && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '10px 12px', background: '#fff', borderRadius: 8, border: '0.5px solid #e5e7eb' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#0D1B2A' }}>{infoSignataire.nom}</div>
                          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                            {infoSignataire.role} · {infoSignataire.date}
                          </div>
                          {!infoSignataire.estTitulaire && (
                            <div style={{ fontSize: 10, color: '#E67E22', marginTop: 2, fontWeight: 600 }}><IconeAlerte taille={11} /> Signature par délégation</div>
                          )}
                        </div>
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 12, lineHeight: 1.5 }}>
                      <IconeAlerte taille={13} /> En confirmant, vous certifiez avoir lu et approuvé ce mémo.
                    </div>
                    <button type='button' onClick={confirmerSignatureDG} disabled={saving}
                      style={{ width: '100%', padding: '11px', borderRadius: 10, border: 'none', background: '#27AE60', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 }}>
                      {saving ? <><IconeHorloge taille={14} /> Envoi...</> : <><IconeValide taille={14} /> Confirmer et transmettre à la Holding</>}
                    </button>
                    <button type='button' onClick={reinitialiserPreview}
                      style={{ width: '100%', padding: '8px', borderRadius: 10, border: '1px solid #ddd', background: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
                      <IconeRafraichir taille={13} /> Annuler
                    </button>
                  </div>
                )}

                {/* ===== APPROUVER/REJETER ===== */}
                {actions.approuver && !actionMode && !actions.upload && !actions.signer && (
                  <div style={s.actionSection}>
                    <div style={s.actionTitle}>Action requise</div>
                    <button type="button" onClick={() => setActionMode('approuver')} style={s.btnApprouver}><IconeValide taille={14} /> Approuver et transmettre</button>
                    {actions.rejeter && <button type="button" onClick={() => setActionMode('rejeter')} style={s.btnRejeterSm}><IconeRefuse taille={13} /> Rejeter</button>}
                  </div>
                )}

                {/* Confirmation approuver */}
                {actionMode === 'approuver' && (
                  <div style={s.actionSection}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1E8449', marginBottom: 10 }}><IconeValide taille={13} /> Confirmer l'approbation</div>
                    <textarea style={s.textarea} placeholder="Commentaire optionnel..." value={commentaire} onChange={e => setCommentaire(e.target.value)} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" onClick={() => { setActionMode(null); setCommentaire(''); }} style={s.btnAnnuler}>Annuler</button>
                      <button type="button" onClick={approuver} disabled={saving} style={s.btnApprouver}>
                        {saving ? <><IconeHorloge taille={14} />...</> : <><IconeValide taille={14} /> Confirmer</>}
                      </button>
                    </div>
                  </div>
                )}

                {/* Confirmation rejeter */}
                {actionMode === 'rejeter' && (
                  <div style={s.actionSection}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#96281B', marginBottom: 10 }}><IconeRefuse taille={13} /> Motif du rejet</div>
                    <textarea style={s.textarea} placeholder="Motif obligatoire..." value={commentaire} onChange={e => setCommentaire(e.target.value)} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" onClick={() => { setActionMode(null); setCommentaire(''); }} style={s.btnAnnuler}>Annuler</button>
                      <button type="button" onClick={rejeter} disabled={saving} style={{ ...s.btnApprouver, background: '#C0392B' }}>
                        {saving ? <><IconeHorloge taille={14} />...</> : <><IconeRefuse taille={14} /> Confirmer le rejet</>}
                      </button>
                    </div>
                  </div>
                )}

                {/* Voir dossier complet */}
                <button type="button" onClick={() => navigate('/demandes/' + (selected.id || selected._id))}
                  style={{ width: '100%', padding: '10px', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', color: '#666', marginTop: 8 }}>
                  <IconeDossier taille={14} /> Voir le dossier complet
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'Inter, system-ui, sans-serif', background: '#F5F6FA', overflow: 'hidden' },
  topbar: { background: '#0D1B2A', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 },
  btnNav: { padding: '6px 14px', borderRadius: 8, border: '0.5px solid rgba(255,255,255,0.2)', background: 'transparent', color: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' },
  main: { flex: 1, display: 'flex', overflow: 'hidden' },
  liste: { width: 280, background: '#fff', borderRight: '0.5px solid #e5e7eb', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  listeTitre: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '0.5px solid #e5e7eb', background: '#F9FAFB', flexShrink: 0 },
  listeItem: { padding: '14px 16px', borderBottom: '0.5px solid #f0f0f0', cursor: 'pointer', background: '#fff' },
  emptyState: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, textAlign: 'center', color: '#888', fontSize: 13 },
  splitVide: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' },
  split: { flex: 1, display: 'flex', overflow: 'hidden' },
  splitLeft: { flex: 1, display: 'flex', flexDirection: 'column', background: '#2D3748', overflow: 'hidden', position: 'relative' },
  onglets: { display: 'flex', background: '#1A202C', borderBottom: '1px solid #4A5568', flexShrink: 0, overflowX: 'auto', alignItems: 'center' },
  onglet: { padding: '10px 18px', fontSize: 12, fontWeight: 500, cursor: 'pointer', border: 'none', background: 'transparent', color: '#A0AEC0', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  ongletActif: { background: '#2D3748', color: '#fff', borderBottom: '2px solid #C0392B' },
  pdfZone: { flex: 1, position: 'relative', overflow: 'hidden' },
  splitRight: { width: 360, display: 'flex', flexDirection: 'column', background: '#fff', borderLeft: '0.5px solid #e5e7eb', overflow: 'hidden' },
  decisionHeader: { background: 'linear-gradient(135deg, #0D1B2A, #1A3A5C)', padding: '18px 20px', flexShrink: 0 },
  decisionBody: { flex: 1, overflowY: 'auto', padding: 16 },
  resumeGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 },
  resumeItem: { background: '#F9FAFB', borderRadius: 8, padding: '8px 12px', border: '0.5px solid #e5e7eb' },
  resumeLabel: { fontSize: 9, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 },
  resumeValue: { fontSize: 12, fontWeight: 600, color: '#0D1B2A' },
  actionSection: { marginBottom: 14 },
  actionTitle: { fontSize: 13, fontWeight: 700, color: '#0D1B2A', marginBottom: 10 },
  textarea: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '0.5px solid #ddd', fontSize: 12, fontFamily: 'inherit', outline: 'none', background: '#F9FAFB', boxSizing: 'border-box', resize: 'none', height: 70, color: '#0D1B2A', marginBottom: 10 },
  btnApprouver: { width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: '#27AE60', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 },
  btnRejeterSm: { width: '100%', padding: '10px', borderRadius: 10, border: '1px solid #C0392B', background: 'transparent', color: '#C0392B', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 },
  btnSigner: { width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #2980B9, #1A5E8A)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 },
  btnUpload: { display: 'block', width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #8E44AD, #6C3483)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8, textAlign: 'center', boxSizing: 'border-box' },
  btnAnnuler: { flex: 1, padding: '10px', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' },
  successMsg: { background: '#EAFAF1', border: '0.5px solid #27AE60', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#1E8449', fontWeight: 600, marginBottom: 12 },
  errorMsg: { background: '#FDEDEC', color: '#96281B', padding: '8px 12px', borderRadius: 8, fontSize: 12, marginBottom: 10 },
};
